import React, { useState } from 'react';
import classnames from 'classnames';
import moize from 'moize';
import PropTypes from 'prop-types';
import { scaleOrdinal as d3ScaleOrdinal } from 'd3-scale';
import { arc as d3Arc, pie as d3Pie } from 'd3-shape';

import style from './styles.css';

const pie = d3Pie().value(d => d.value);

const getCachedArc = moize.deep(
  (width, height, anglePadding, ringWidth, cornerRadius, margin) => {
    const radius = Math.min(width, height) / 2;
    const outerRadius = radius - margin;

    return d3Arc()
      .padAngle(anglePadding)
      .outerRadius(outerRadius)
      .innerRadius(outerRadius - ringWidth)
      .cornerRadius(cornerRadius);
  }
);

const getCachedColorForLabel = moize.deep((data, colors) =>
  d3ScaleOrdinal()
    .domain(data.map(({ label }) => label))
    .range(colors)
);

const getCachedPieData = moize.deep(data => pie(data));

/**
  DonutChart component - pie chart with
  percentage values, labels and colors for different categories.
*/
const DonutChart = props => {
  const [activeSegment, activateSegment] = useState(null);

  const {
    anglePadding,
    colors,
    cornerRadius,
    data,
    height,
    margin,
    ringWidth,
    showActiveLabel,
    showLabels,
    title,
    valueFormatter,
    width
  } = props;

  const getArc = () => {
    return getCachedArc(
      width,
      height,
      anglePadding,
      ringWidth,
      cornerRadius,
      margin
    );
  };

  const getColorForLabel = () => {
    return getCachedColorForLabel(data, colors);
  };

  const getPieData = () => {
    return getCachedPieData(data);
  };

  const activeSegmentData =
    activeSegment && data.find(({ label }) => label === activeSegment).value;

  const showTitle = (!showActiveLabel || !activeSegment) && title;
  const showActiveSegment = showActiveLabel && activeSegment;

  return (
    <div className={classnames('pie-chart', { 'has-active': activeSegment })}>
      <div
        className="center-text"
        style={{ width, height, padding: margin + ringWidth }}>
        {showTitle && <span className="title">{title}</span>}
        {showActiveSegment && (
          <span className="active-segment-label">
            {activeSegment}
            <br />
            <strong>{valueFormatter(activeSegmentData)}</strong>
          </span>
        )}
      </div>

      <svg width={width} height={height}>
        <g transform={`translate(${width / 2},${height / 2})`}>
          {getPieData().map(slice => (
            <g
              onMouseEnter={() => activateSegment(slice.data.label)}
              onMouseLeave={() => activateSegment(null)}
              key={`arc-${slice.data.label}`}
              className={classnames('arc', {
                active: activeSegment === slice.data.label
              })}>
              <path
                d={getArc()(slice)}
                fill={slice.data.color || getColorForLabel()(slice.data.label)}
              />
              {showLabels && (
                <text
                  transform={`translate(${getArc().centroid(slice)})`}
                  className="segment-label">
                  {slice.data.label}
                </text>
              )}
            </g>
          ))}
        </g>
      </svg>
      <style jsx>{style}</style>
    </div>
  );
};

DonutChart.defaultProps = {
  anglePadding: 0.01,
  width: 300,
  height: 300,
  valueFormatter: x => x,
  colors: ['#004b71', '#ffdf00', '#e37a00', '#c03123', '#614a7a', '#7c993f'],
  cornerRadius: 2,
  margin: 10,
  ringWidth: 10,
  showActiveLabel: true,
  showLabels: false
};

DonutChart.propTypes = {
  /**
      Padding of each slice angle useful for adding space between slices
    */
  anglePadding: PropTypes.number,
  /**
      segment data needed to render the chart
    */
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string
    })
  ).isRequired,
  /**
      width of the pie chart
    */
  width: PropTypes.number,
  /**
      height the pie chart
    */
  height: PropTypes.number,
  /**
      function that formats the value for display
    */
  valueFormatter: PropTypes.func,
  /**
      array of colors in hex format
    */
  colors: PropTypes.arrayOf(PropTypes.string),
  /**
      border/corner radius of each arc segment
    */
  cornerRadius: PropTypes.number,
  /**
      Margin from the exterior of the svg chart to the ring
    */
  margin: PropTypes.number,
  /**
      width in pixels of the ring
    */
  ringWidth: PropTypes.number,
  /**
      boolean that represents whether the active segment label and
      value should be seen in the center text
    */
  showActiveLabel: PropTypes.bool,
  /**
      boolean which represents whether arc segments should be labeled
    */
  showLabels: PropTypes.bool
};

export default DonutChart;
